import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  User,
  ChevronRight,
} from 'lucide-react';
import RefundDetails from './RefundDetails';

/**
 * RefundApprovalPanel - Panel for viewing and approving pending refunds
 *
 * Props:
 * - currentUser: object - Current user info
 * - onRefundProcessed: function - Called when a refund is approved/rejected
 */
const RefundApprovalPanel = ({ currentUser, onRefundProcessed }) => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingRefunds, setPendingRefunds] = useState([]);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [refundToReject, setRefundToReject] = useState(null);

  const reasonLabels = {
    duplicate_charge: 'Duplicate Charge',
    service_not_provided: 'Service Not Provided',
    billing_error: 'Billing Error',
    customer_request: 'Customer Request',
    subscription_cancellation: 'Subscription Cancellation',
    other: 'Other',
  };

  useEffect(() => {
    loadPendingRefunds();
  }, []);

  const loadPendingRefunds = async () => {
    try {
      setLoading(true);
      setError('');

      // Use admin endpoint with status filter for pending refunds
      const response = await axios.get(
        `${getApiBase()}/admin/billing/refunds`,
        {
          headers: getAuthHeaders(),
          params: { status: 'pending', limit: 50 }
        }
      );

      setPendingRefunds(response.data.refunds || []);
    } catch (err) {
      console.error('Error loading pending refunds:', err);
      // Don't show error if just no permission (might be expected)
      if (err.response?.status !== 403) {
        setError(err.response?.data?.detail || 'Failed to load pending refunds');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const handleApprove = async (refund) => {
    try {
      setProcessing(true);
      setError('');

      // Use admin endpoint for approval
      await axios.post(
        `${getApiBase()}/admin/billing/refunds/${refund.id}/approve`,
        null,
        {
          headers: getAuthHeaders(),
          params: { approved: true }
        }
      );

      setSuccess('Refund approved successfully');
      setTimeout(() => setSuccess(''), 3000);

      // Refresh list
      loadPendingRefunds();

      // Close detail modal if open
      setSelectedRefund(null);

      // Notify parent
      if (onRefundProcessed) {
        onRefundProcessed();
      }
    } catch (err) {
      console.error('Error approving refund:', err);
      setError(err.response?.data?.detail || 'Failed to approve refund');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = (refund) => {
    setRefundToReject(refund);
    setRejectNotes('');
    setShowRejectModal(true);
    setSelectedRefund(null);
  };

  const confirmReject = async () => {
    if (!refundToReject) return;

    try {
      setProcessing(true);
      setError('');

      // Use admin endpoint for rejection
      await axios.post(
        `${getApiBase()}/admin/billing/refunds/${refundToReject.id}/approve`,
        null,
        {
          headers: getAuthHeaders(),
          params: {
            approved: false,
            rejection_notes: rejectNotes || undefined,
          }
        }
      );

      setSuccess('Refund rejected');
      setTimeout(() => setSuccess(''), 3000);

      // Close modal and refresh
      setShowRejectModal(false);
      setRefundToReject(null);
      loadPendingRefunds();

      // Notify parent
      if (onRefundProcessed) {
        onRefundProcessed();
      }
    } catch (err) {
      console.error('Error rejecting refund:', err);
      setError(err.response?.data?.detail || 'Failed to reject refund');
    } finally {
      setProcessing(false);
    }
  };

  // Check if user can approve a specific refund
  const canApproveRefund = (refund) => {
    if (!currentUser) return false;
    // Can't approve own request
    if (refund.requested_by_id === currentUser.id) return false;
    // Can't provide both approvals
    if (refund.first_approved_by_id === currentUser.id) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading pending refunds...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Pending Refund Approvals
            </h3>
            <p className="text-sm text-gray-500">
              {pendingRefunds.length} refund{pendingRefunds.length !== 1 ? 's' : ''} awaiting approval
            </p>
          </div>
          <button
            onClick={loadPendingRefunds}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
              <span className="text-green-800">{success}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center">
              <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Refund List */}
        {pendingRefunds.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-300" />
            <p>No pending refund approvals</p>
            <p className="text-sm mt-1">All refund requests have been processed</p>
          </div>
        ) : (
          <div className="divide-y">
            {pendingRefunds.map((refund) => {
              const canApprove = canApproveRefund(refund);
              const needsSecondApproval = refund.status === 'pending_second_approval';

              return (
                <div
                  key={refund.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedRefund(refund)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          needsSecondApproval ? 'bg-orange-100' : 'bg-yellow-100'
                        }`}>
                          <DollarSign className={`w-5 h-5 ${
                            needsSecondApproval ? 'text-orange-600' : 'text-yellow-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {formatCurrency(refund.amount, refund.currency)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {reasonLabels[refund.reason] || refund.reason}
                          </p>
                        </div>
                      </div>

                      <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          {refund.requested_by_name}
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(refund.created_at)}
                        </span>
                        {needsSecondApproval && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Needs 2nd Approval
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {canApprove ? (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(refund);
                            }}
                            disabled={processing}
                            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReject(refund);
                            }}
                            disabled={processing}
                            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 italic">
                          {refund.requested_by_id === currentUser?.id
                            ? 'Own request'
                            : 'Already approved'}
                        </span>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refund Details Modal */}
      {selectedRefund && (
        <RefundDetails
          refund={selectedRefund}
          onClose={() => setSelectedRefund(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          canApprove={canApproveRefund(selectedRefund)}
          currentUserId={currentUser?.id}
        />
      )}

      {/* Reject Confirmation Modal */}
      {showRejectModal && refundToReject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">Reject Refund</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-600">
                Are you sure you want to reject this refund request for{' '}
                <strong>{formatCurrency(refundToReject.amount, refundToReject.currency)}</strong>?
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rejection Reason (optional)
                </label>
                <textarea
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="Provide a reason for rejecting this refund..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRefundToReject(null);
                }}
                className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={processing}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject Refund
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundApprovalPanel;
